/**
 * Gallery service for image selection from device gallery
 * Provides file selection with type filtering, validation, and processing
 */

import { logger } from '../utils/logger';

export interface GalleryOptions {
  accept?: string; // MIME types, default: 'image/*'
  multiple?: boolean; // Allow multiple file selection, default: false
  maxSize?: number; // Maximum file size in bytes, default: 10MB
  maxWidth?: number; // Maximum image width for compression, default: 1920
  maxHeight?: number; // Maximum image height for compression, default: 1920
  quality?: number; // JPEG compression quality 0-1, default: 0.8
  compressLargeImages?: boolean; // Auto-compress images > 1MB, default: true
}

export interface GalleryResult {
  files: File[];
  totalSize: number;
  errors: string[];
}

export class GalleryService {
  /**
   * Validate file type against accepted MIME types
   * @param file - File to validate
   * @param accept - Accepted MIME types pattern
   * @returns boolean indicating if file is valid
   */
  private validateFileType(file: File, accept: string): boolean {
    if (accept === '*/*') return true;
    
    const acceptedTypes = accept.split(',').map(type => type.trim());
    
    return acceptedTypes.some(acceptedType => {
      if (acceptedType.endsWith('/*')) {
        // Handle wildcard types like 'image/*'
        const baseType = acceptedType.slice(0, -2);
        return file.type.startsWith(baseType);
      }
      return file.type === acceptedType;
    });
  }

  /**
   * Validate file size against maximum allowed size
   * @param file - File to validate
   * @param maxSize - Maximum size in bytes
   * @returns boolean indicating if file size is valid
   */
  private validateFileSize(file: File, maxSize: number): boolean {
    return file.size <= maxSize;
  }

  /**
   * Compress an image file if it's too large
   * @param file - Image file to compress
   * @param options - Compression options
   * @returns Promise resolving to compressed File or original
   */
  private async compressImage(file: File, options: GalleryOptions): Promise<File> {
    // Skip compression if disabled or file is small
    if (!options.compressLargeImages || file.size <= 1_048_576) {
      return file;
    }

    logger.debug('[GalleryService] Compressing image', {
      name: file.name,
      originalSize: (file.size / 1024 / 1024).toFixed(2) + ' MB',
      targetQuality: options.quality || 0.8,
    });

      return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();

                  img.onload = () => {
            const maxWidth = options.maxWidth || 1920;
            const maxHeight = options.maxHeight || 1920;
            
            let { width, height } = img;
            
            // Calculate scaling factor to fit within bounds
            const scale = Math.min(
              maxWidth / width,
              maxHeight / height,
              1 // Don't upscale
            );
            
            width = Math.floor(width * scale);
            height = Math.floor(height * scale);

          // Create canvas for compression
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }

          // Draw scaled image
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }

                const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                  lastModified: Date.now(),
                });

              logger.debug('[GalleryService] Compression complete', {
                  name: file.name,
                  originalSize: (file.size / 1024 / 1024).toFixed(2) + ' MB',
                  compressedSize: (compressedFile.size / 1024 / 1024).toFixed(2) + ' MB',
                reduction: ((1 - compressedFile.size / file.size) * 100).toFixed(1) + '%',
                dimensions: `${width}x${height}`,
                });

                resolve(compressedFile);
            },
            'image/jpeg',
            options.quality || 0.8
          );
        };

        img.onerror = () => {
          reject(new Error('Failed to load image for compression'));
        };

        img.src = e.target?.result as string;
      };

      reader.onerror = () => {
        reject(new Error('Failed to read image file'));
      };

      reader.readAsDataURL(file);
      });
  }

  /**
   * Process selected files with validation and compression
   * @param files - FileList from input element
   * @param options - Processing options
   * @returns Promise resolving to GalleryResult
   */
  private async processFiles(files: FileList, options: GalleryOptions): Promise<GalleryResult> {
    const result: GalleryResult = {
      files: [],
      totalSize: 0,
      errors: [],
    };

    const maxSize = options.maxSize || 10 * 1024 * 1024; // 10MB default
    const accept = options.accept || 'image/*';

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      try {
        // Validate file type
        if (!this.validateFileType(file, accept)) {
          result.errors.push(`File "${file.name}" has unsupported type: ${file.type}`);
          continue;
        }

        // Validate file size
        if (!this.validateFileSize(file, maxSize)) {
          result.errors.push(
            `File "${file.name}" is too large: ${(file.size / 1024 / 1024).toFixed(2)}MB (max: ${(maxSize / 1024 / 1024).toFixed(2)}MB)`
          );
          continue;
        }

        // Process the file (compress if image and enabled)
        let processedFile = file;
        if (file.type.startsWith('image/') && options.compressLargeImages !== false) {
          try {
            processedFile = await this.compressImage(file, options);
          } catch (error) {
            logger.warn('[GalleryService] Compression failed, using original', error as Error);
            // Continue with original file if compression fails
          }
        }

        result.files.push(processedFile);
        result.totalSize += processedFile.size;
      } catch (error) {
        result.errors.push(`Error processing file "${file.name}": ${error}`);
      }
    }

    return result;
  }

  /**
   * Open gallery picker for image/file selection
   * @param options - Gallery options including file type filters and processing settings
   * @returns Promise resolving to GalleryResult with selected and processed files
   * @throws Error if user cancels selection or no files are selected
   */
  async selectImages(options: GalleryOptions = {}): Promise<GalleryResult> {
    logger.debug('[GalleryService] selectImages() called with options', { options });

    // Create hidden file input element
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = options.accept || 'image/*';
    input.multiple = options.multiple || false;

    logger.debug('[GalleryService] Input element created', {
      type: input.type,
      accept: input.accept,
      multiple: input.multiple,
    });

    // Hide the input element
    input.style.position = 'fixed';
    input.style.left = '-10000px';
    input.style.top = '-10000px';

    return new Promise((resolve, reject) => {
      // Handle file selection
      input.onchange = async (event) => {
        const target = event.target as HTMLInputElement;
        const files = target.files;

        // Clean up: remove input from DOM
        input.remove();

        if (files && files.length > 0) {
          logger.debug('[GalleryService] Files selected', {
          count: files.length,
          files: Array.from(files).map(f => ({
            name: f.name,
            size: (f.size / 1024).toFixed(2) + ' KB',
            type: f.type,
          })),
        });

        try {
          const result = await this.processFiles(files, options);
          
            logger.debug('[GalleryService] Files processed', {
             successful: result.files.length,
             errors: result.errors.length,
             totalSize: (result.totalSize / 1024).toFixed(2) + ' KB',
           });

            if (result.files.length === 0 && result.errors.length > 0) {
              reject(new Error(`No valid files selected: ${result.errors.join(', ')}`));
            } else {
          resolve(result);
            }
        } catch (error) {
            reject(new Error(`Error processing files: ${error}`));
          }
        } else {
          reject(new Error('No files selected'));
        }
      };

      // Handle cancel: Use focus event with timeout
      const handleCancel = () => {
        setTimeout(() => {
          if (!input.files?.length) {
            input.remove();
            reject(new Error('Gallery selection cancelled'));
          }
        }, 300);
      };

      // Add to DOM temporarily (required for some browsers)
      document.body.appendChild(input);
      logger.debug('[GalleryService] Input added to DOM');

      // Listen for window focus (indicates dialog closed)
      window.addEventListener('focus', handleCancel, { once: true });

      // Trigger the file picker
      logger.debug('[GalleryService] Triggering file picker...');
      input.click();
      logger.debug('[GalleryService] input.click() called');
    });
  }

  /**
   * Select a single image file
   * @param options - Gallery options
   * @returns Promise resolving to the selected File or null if cancelled
   */
  async selectImage(options: Omit<GalleryOptions, 'multiple'> = {}): Promise<File | null> {
    try {
      const result = await this.selectImages({ ...options, multiple: false });
      return result.files[0] || null;
    } catch (error) {
      if (error instanceof Error && error.message.includes('cancelled')) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Select multiple image files
   * @param options - Gallery options
   * @returns Promise resolving to array of selected Files
   */
  async selectMultipleImages(options: Omit<GalleryOptions, 'multiple'> = {}): Promise<File[]> {
    const result = await this.selectImages({ ...options, multiple: true });
    return result.files;
  }

  /**
   * Create a preview URL for an image file
   * @param file - Image file to create preview for
   * @returns Object URL that can be used as src for img elements
   */
  createPreviewUrl(file: File): string {
    return URL.createObjectURL(file);
  }

  /**
   * Revoke a preview URL to free memory
   * @param url - Object URL to revoke
   */
  revokePreviewUrl(url: string): void {
    URL.revokeObjectURL(url);
  }

  /**
   * Get image dimensions from a file
   * @param file - Image file to analyze
   * @returns Promise resolving to width and height
   */
  async getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = this.createPreviewUrl(file);

      img.onload = () => {
        this.revokePreviewUrl(url);
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };

      img.onerror = () => {
        this.revokePreviewUrl(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  }
}

// Export singleton instance
export const galleryService = new GalleryService();